import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Mail, Lock, User, Eye, EyeOff, Check, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState('plan'); // 'plan' or 'info'
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    contact_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const plans = {
    monthly: {
      id: 'monthly',
      label: 'Mensuel',
      price: 149,
      priceLabel: '149€',
      period: '/mois',
      description: 'Facturation mensuelle',
      features: ['Accès complet', 'Commerciaux illimités', 'Export PDF/Excel', 'Support email'],
    },
    annual: {
      id: 'annual',
      label: 'Annuel',
      price: 126,
      priceLabel: '126€',
      period: '/mois',
      description: 'Facturation annuelle',
      totalLabel: '1 512€/an',
      savings: '-15%',
      features: ['Accès complet', 'Commerciaux illimités', 'Export PDF/Excel', 'Support prioritaire'],
    },
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
  };

  const handleContinueToInfo = () => {
    setStep('info');
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password || !formData.contact_name) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/company/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          contact_name: formData.contact_name,
          plan: selectedPlan,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }

      // Redirect to Stripe
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Erreur lors de la création de la session de paiement');
      }
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center justify-between px-4 sm:px-6">
        <h1 className="text-xl sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Litige-Protect <span className="text-accent">Admin</span>
        </h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="header-login-btn"
            onClick={() => navigate('/admin')}
            className="px-4 py-2 border border-accent text-accent text-sm font-medium rounded-md hover:bg-accent/10 transition-all"
          >
            Se connecter
          </button>
          <button
            data-testid="header-register-btn"
            onClick={() => { setStep('plan'); window.scrollTo(0, 0); }}
            className="px-4 py-2 bg-accent text-accent-foreground text-sm font-medium rounded-md hover:bg-accent/90 transition-all"
          >
            S'inscrire
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-2xl space-y-6">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className={`flex items-center gap-2 ${step === 'plan' ? 'text-accent font-semibold' : 'text-foreground/40'}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${step === 'plan' ? 'bg-accent text-white' : 'bg-secondary text-foreground/60'}`}>1</span>
              Abonnement
            </div>
            <div className="w-8 h-px bg-border" />
            <div className={`flex items-center gap-2 ${step === 'info' ? 'text-accent font-semibold' : 'text-foreground/40'}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${step === 'info' ? 'bg-accent text-white' : 'bg-secondary text-foreground/60'}`}>2</span>
              Informations
            </div>
            <div className="w-8 h-px bg-border" />
            <div className="flex items-center gap-2 text-foreground/40">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm bg-secondary text-foreground/60">3</span>
              Paiement
            </div>
          </div>

          {/* Plan Selection */}
          {step === 'plan' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                  <CreditCard className="w-8 h-8 text-accent" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Choisissez votre abonnement
                </h2>
                <p className="text-sm text-foreground/60 mt-2">
                  Accédez à toutes les fonctionnalités de Litige-Protect
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.values(plans).map((plan) => (
                  <button
                    key={plan.id}
                    data-testid={`plan-${plan.id}`}
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`relative bg-card border-2 rounded-xl p-5 text-left transition-all ${
                      selectedPlan === plan.id
                        ? 'border-accent shadow-lg shadow-accent/10'
                        : 'border-border hover:border-accent/40'
                    }`}
                  >
                    {plan.savings && (
                      <span className="absolute -top-3 right-4 px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                        {plan.savings}
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-lg">{plan.label}</span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlan === plan.id ? 'border-accent bg-accent' : 'border-foreground/30'
                      }`}>
                        {selectedPlan === plan.id && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <div className="mb-3">
                      <span className="text-3xl font-bold">{plan.priceLabel}</span>
                      <span className="text-foreground/60 text-sm">{plan.period}</span>
                    </div>
                    {plan.totalLabel && (
                      <p className="text-sm text-foreground/50 mb-3">soit {plan.totalLabel}</p>
                    )}
                    <p className="text-sm text-foreground/60 mb-4">{plan.description}</p>
                    <ul className="space-y-2">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>

              <button
                data-testid="continue-to-info-btn"
                onClick={handleContinueToInfo}
                className="w-full h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 transition-all"
              >
                Continuer
              </button>
            </div>
          )}

          {/* Company Info Form */}
          {step === 'info' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                  <Building2 className="w-8 h-8 text-accent" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Informations de l'entreprise
                </h2>
                <p className="text-sm text-foreground/60 mt-2">
                  Abonnement sélectionné : <span className="font-semibold text-accent">{plans[selectedPlan].label} - {plans[selectedPlan].priceLabel}{plans[selectedPlan].period}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nom de l'entreprise *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                      <input
                        data-testid="register-company-name"
                        type="text" name="name" value={formData.name} onChange={handleChange}
                        placeholder="Ma Société Solaire"
                        className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Votre nom *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                      <input
                        data-testid="register-contact-name"
                        type="text" name="contact_name" value={formData.contact_name} onChange={handleChange}
                        placeholder="Jean Dupont"
                        className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                      <input
                        data-testid="register-email"
                        type="email" name="email" value={formData.email} onChange={handleChange}
                        placeholder="contact@entreprise.fr"
                        className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Mot de passe *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                      <input
                        data-testid="register-password"
                        type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange}
                        placeholder="Min. 6 caractères"
                        className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-12 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                        required minLength={6}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirmer le mot de passe *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" strokeWidth={1.5} />
                      <input
                        data-testid="register-confirm-password"
                        type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
                        placeholder="Confirmer"
                        className="w-full h-12 bg-white border border-input rounded-md pl-10 pr-4 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Recap */}
                <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground/70">Abonnement {plans[selectedPlan].label}</span>
                    <span className="font-bold text-foreground">{plans[selectedPlan].priceLabel}{plans[selectedPlan].period}</span>
                  </div>
                  {plans[selectedPlan].totalLabel && (
                    <div className="flex items-center justify-between text-xs text-foreground/50 mt-1">
                      <span>Total annuel</span>
                      <span>{plans[selectedPlan].totalLabel}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep('plan')}
                    className="flex-1 h-12 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm font-medium">
                    Modifier le plan
                  </button>
                  <button
                    data-testid="register-submit-btn"
                    type="submit" disabled={loading}
                    className="flex-[2] h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    <CreditCard className="w-5 h-5" />
                    {loading ? 'Redirection...' : 'Procéder au paiement'}
                  </button>
                </div>
              </form>

              <div className="text-center space-y-3">
                <p className="text-sm text-foreground/60">
                  Déjà inscrit ?{' '}
                  <Link to="/admin" className="text-accent hover:underline font-medium">Se connecter</Link>
                </p>
                <button onClick={() => navigate('/')} className="text-sm text-foreground/60 hover:text-accent">
                  Retour à l'accueil
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
